/**
 * DemoWorkflow: true interactive fake workflow demo.
 *
 * Renders a fake mobile device with a home screen of tappable app icons.
 * Each scenario in workflow/scripts.js defines which apps appear, the entry
 * app, seed dashboard state, and any auto-triggered warnings.  All state is
 * held here in React; no network calls, no writes.
 *
 * Public surface:
 *   <DemoWorkflow scenario={scenario} managerView resetToken onComplete/>
 *   scenario.workflow → workflow object from scripts.js
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  MessageCircle, Phone, Send, Store, ClipboardList,
  ChevronLeft, ArrowRight, AlertTriangle, CheckCircle2, Car, Wrench,
  User, Circle, ArrowLeft, Wifi, Plus, Truck, RotateCcw,
  ClipboardCheck, AlertOctagon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DemoBadge } from "../DemoPanels";
import DesktopShell from "./DesktopShell";
import DeviceShell from "./DeviceShell";
import WalkInIntro from "./WalkInIntro";
import BusinessPageApp from "./BusinessPageApp";
import WebsiteApp from "./WebsiteApp";
import ScenarioSummary from "./ScenarioSummary";
import CustomWorkflow from "./CustomWorkflow";

/* ---------- APP CATALOGUE ---------- */

const APPS = {
  channel:      { key: "channel",       Icon: null,             color: "" }, // resolved dynamically
  notepad:      { key: "notepad",       img: "/icon-notepad-192.png",   label: "ConfirmPH Notepad" },
  dashboard:    { key: "dashboard",     img: "/icon-dashboard-192.png", label: "ConfirmPH Business" },
  "business-page": { key: "business-page", img: "/confirmph-icon.png",   label: "Business Page" },
  website:      { key: "website",       img: "/confirmph-icon.png",     label: "Business Website" },
};
const CHANNEL_META = {
  messenger: { Icon: MessageCircle, color: "bg-sky-500",     label: "Messenger",   bubble: "bg-sky-100 text-sky-900",     accent: "text-sky-700" },
  whatsapp:  { Icon: Send,          color: "bg-emerald-500", label: "WhatsApp",    bubble: "bg-emerald-100 text-emerald-900", accent: "text-emerald-700" },
  sms:       { Icon: MessageCircle, color: "bg-slate-500",   label: "SMS",         bubble: "bg-slate-200 text-slate-900", accent: "text-slate-700" },
  phone:     { Icon: Phone,         color: "bg-indigo-500",  label: "Phone",       bubble: "bg-indigo-100 text-indigo-900", accent: "text-indigo-700" },
  walkin:    { Icon: Store,         color: "bg-amber-500",   label: "Walk-in",     bubble: "bg-amber-100 text-amber-900", accent: "text-amber-700" },
};

/* ---------- MAIN ---------- */

export default function DemoWorkflow({
  scenario, managerView, resetToken, autoPlay = false, deviceMode = "desktop",
  audience = "promoter", embedded = false,
  onAutoPlayEnd, onComplete,
}) {
  const wf = scenario.workflow;
  const navigate = useNavigate();

  // Screen stack: persists in-scenario navigation.  activeApp is the current
  // app; back returns to home.  Detail views push onto `route`.
  const [activeApp, setActiveApp] = useState(wf.entry || "home");
  const [route, setRoute] = useState([]);        // e.g. ['booking:b-1'] or ['warning'] or ['confirmation']
  const [notepad, setNotepad] = useState(wf.notepad?.initial || {});
  const [draft, setDraft] = useState(null);
  const [bookings, setBookings] = useState((wf.bookings || []).map(b => ({ ...b })));
  const [warning, setWarning] = useState(null);
  const [vehicles, setVehicles] = useState((wf.vehicles || []).map(v => ({ ...v })));
  const [replacement, setReplacement] = useState(null);   // { model, plate } once assigned
  const [confirmed, setConfirmed] = useState(false);      // messenger flows
  const [settled, setSettled] = useState(false);          // deposit flows
  const [linkForCompose, setLinkForCompose] = useState(null); // pending outgoing link
  const [channelExtras, setChannelExtras] = useState([]);     // extra chat bubbles
  // How many of wf.channel.messages have been revealed. Lifted up here
  // (rather than local to ChannelApp) so switching to another app and back
  // to "channel" doesn't replay the whole conversation: ChannelApp fully
  // unmounts on navigation, so this needs to live where it survives that.
  const [channelShown, setChannelShown] = useState(0);
  // Terminal-state key for the end-of-scenario recap.  Set once a scenario
  // reaches a meaningful conclusion (customer confirmed the link, deposit
  // was settled, warning was resolved by reassigning a vehicle, walk-in
  // booking was drafted, etc.).  Reset with the rest of the scenario state.
  const [outcome, setOutcome] = useState(null);

  // Scripted image intro (currently: walk-in scenario).  When wf.intro is
  // defined the promoter walks through it before touching the software.
  const hasIntro = Array.isArray(wf.intro) && wf.intro.length > 0;
  const [introStep, setIntroStep] = useState(0);
  const [introDone, setIntroDone] = useState(!hasIntro);

  // Reset on resetToken change.
  useEffect(() => {
    setActiveApp(wf.entry || "home");
    setRoute([]);
    setNotepad(wf.notepad?.initial || {});
    setDraft(null);
    setBookings((wf.bookings || []).map(b => ({ ...b })));
    setWarning(wf.bannerWarn ? { ...wf.bannerWarn } : null);
    setVehicles((wf.vehicles || []).map(v => ({ ...v })));
    setReplacement(null);
    setConfirmed(false);
    setSettled(false);
    setLinkForCompose(null);
    setChannelExtras([]);
    setChannelShown(0);
    setIntroStep(0);
    setIntroDone(!hasIntro);
    setOutcome(null);
  }, [resetToken, wf, hasIntro]);

  // Signal completion for the outer stage.
  useEffect(() => {
    if (confirmed || settled || replacement) onComplete?.();
  }, [confirmed, settled, replacement, onComplete]);

  // Custom-kind scenarios use the CustomWorkflow driver (which mounts its
  // own DesktopShell + step controls).  Everything else keeps the original
  // stateful workflow below.
  if (wf.kind === "custom") {
    return (
      <CustomWorkflow scenario={scenario} managerView={managerView}
                       resetToken={resetToken} deviceMode={deviceMode}
                       audience={audience} embedded={embedded} onComplete={onComplete} />
    );
  }

  /* --------- actions --------- */
  const openApp = (key) => { setActiveApp(key); setRoute([]); };
  const goHome  = () => { setActiveApp("home"); setRoute([]); };
  const push    = (r) => setRoute(rs => [...rs, r]);
  const pop     = () => setRoute(rs => rs.slice(0, -1));

  const createDraftFromNotepad = () => {
    const tpl = wf.createdDraftTemplate || {};
    const merged = { id: "draft-1", ...tpl, ...pickTruthy(notepad, {
      customer: "customer", vehicle: "vehicle", pickup: "pickup", ret: "ret",
      price: "price", deposit: "deposit", notes: "notes",
    })};
    setDraft(merged);
    setActiveApp("dashboard");
    setRoute([`draft:${merged.id}`]);
    // Walk-in scenario doesn't send a confirmation link: creating the
    // booking record from the counter IS the terminal state.
    if (wf.kind === "intake" && !wf.channel) setOutcome("drafted");
  };

  const promoteDraftToBooking = () => {
    if (!draft) return;
    // Booking exists but no confirmation link has been sent yet: dashboard
    // treats this as "Awaiting confirmation" so promoters see the difference
    // between a booking that's ready and one that still needs the link.
    const b = { ...draft, status: "Awaiting confirmation" };
    setBookings(bs => [...bs.filter(x => x.id !== b.id), b]);
    setDraft(null);
    setRoute([`booking:${b.id}`]);
  };

  // Step 1: staff taps "Send Confirmation Link" on the booking detail.
  // A visible copyable link card appears (route push).
  const generateConfirmationLink = (bId) => {
    setBookings(bs => bs.map(b => b.id === bId
      ? { ...b, status: "Confirmation link ready", _linkGenerated: true }
      : b));
    push(`send-link:${bId}`);
  };

  // Step 2: staff taps Copy on the link card.  We stage the link as the next
  // outgoing staff message inside the channel app so the promoter can literally
  // demo "paste and send" from the messenger side.
  const copyConfirmationLink = (bId) => {
    const url = `confirmph.app/c/DEMO-${bId.replace(/[^a-z0-9]/gi, "").toUpperCase()}`;
    setLinkForCompose(url);
    if (wf.channel) {
      // For intake scenarios: go back to the source channel where the
      // conversation started.
      setActiveApp("channel");
      setRoute([]);
    } else {
      // No external channel (dashboard-first scenarios): jump straight to
      // the customer preview after "sending" the link.
      setBookings(bs => bs.map(b => b.id === bId ? { ...b, status: "Confirmation sent" } : b));
      setLinkForCompose(null);
      setActiveApp("dashboard");
      setRoute([`booking:${bId}`, "confirmation-preview"]);
    }
  };

  // Step 3: staff taps Send inside the channel.  The link becomes a real
  // staff bubble in the transcript; customer then "receives" it and can tap
  // "Open confirmation" to jump to the preview.
  const sendLinkInChannel = () => {
    if (!linkForCompose) return;
    const now = Date.now();
    setChannelExtras(ex => [
      ...ex,
      { from: "staff",    text: `Here's your confirmation link po: ${linkForCompose}`, at: now, key: "staff-link" },
      { from: "customer", text: "Received po, opening the link now.", at: now + 900, key: "cust-received" },
    ]);
    setLinkForCompose(null);
    // Mark the booking that received this link as "Confirmation sent".
    setBookings(bs => bs.map(b =>
      b._linkGenerated ? { ...b, status: "Confirmation sent" } : b));
  };

  // Step 4: customer taps "Open confirmation" in the channel.  Navigate to
  // the customer-side preview screen inside the ConfirmPH Business app.
  const openConfirmationPreview = () => {
    const b = bookings.find(x => x.status === "Confirmation sent") || bookings[bookings.length - 1];
    if (!b) return;
    setActiveApp("dashboard");
    setRoute([`booking:${b.id}`, "confirmation-preview"]);
  };

  const sendConfirmation = (bId) => {
    // Legacy quick-send (retained for any callers that skip the link flow).
    setBookings(bs => bs.map(b => b.id === bId ? { ...b, status: "Confirmation sent" } : b));
    push("confirmation-preview");
  };

  const customerConfirms = (bId) => {
    setBookings(bs => bs.map(b => b.id === bId ? { ...b, status: "Customer Confirmed" } : b));
    setConfirmed(true);
    setRoute([`booking:${bId}`]);
    setOutcome("confirmed");
  };

  const customerRequestsChange = (bId) => {
    setBookings(bs => bs.map(b => b.id === bId ? { ...b, status: "Change requested" } : b));
    setRoute([`booking:${bId}`]);
    setOutcome("requested_change");
  };

  const customerDeclines = (bId) => {
    setBookings(bs => bs.map(b => b.id === bId ? { ...b, status: "Declined by customer" } : b));
    setRoute([`booking:${bId}`]);
    setOutcome("declined");
  };

  const createConflictingDraft = () => {
    // used by double-booking scenario
    const inc = wf.incomingDraft;
    if (!inc) return;
    setDraft({ id: "draft-clash", ...inc });
    setWarning({ ...wf.conflict });
    setActiveApp("dashboard");
    setRoute(["draft:draft-clash"]);
  };

  const reassignVehicle = (source = "smart", manualPick = null) => {
    const smartTarget = wf.conflict?.replacement || wf.reassignedVehicle || wf.onMarkMaintenance?.replacement || wf.bannerWarn?.replacement;
    const target = source === "manual" && manualPick ? manualPick : smartTarget;
    if (!target) return;
    setReplacement({ ...target, _source: source });
    if (draft) {
      const upd = { ...draft, vehicle: `${target.model} · ${target.plate}`, status: "Draft (reassigned)" };
      setDraft(upd);
    }
    setBookings(bs => bs.map(b => ({
      ...b,
      vehicle: replacementApplies(b, wf) ? `${target.model} · ${target.plate}` : b.vehicle,
      status: replacementApplies(b, wf) ? "Vehicle changed" : b.status,
    })));
    setWarning(null);
    // "vehicle-issue" scenarios (breakdown, replacement) end here.
    // Booking-conflict scenarios (double-booking, tight-buffer, late-return)
    // also end here since the conflict has been resolved.
    setOutcome(wf.kind === "vehicle-issue" ? "replacement_assigned" : "resolved_conflict");
  };

  const markMaintenance = (plate) => {
    setVehicles(vs => vs.map(v => v.plate === plate ? { ...v, status: "Maintenance: problem reported" } : v));
    if (wf.onMarkMaintenance) setWarning({ ...wf.onMarkMaintenance });
  };

  const recordBalancePaid = (bId) => {
    setBookings(bs => bs.map(b => b.id === bId ? { ...b, balance: "₱0", status: "Paid: recorded" } : b));
    setSettled(true);
    setOutcome("balance_settled");
  };

  /* --------- render --------- */
  const availableApps = (wf.home || []).map(key => resolveApp(key, wf));

  // Compute the terminal-state summary the moment we're actually in a
  // recap-worthy state.  Scenarios without a `summaries` map (or without
  // a matching outcome) get no summary card: silence is fine.
  const summary = outcome && wf.summaries ? wf.summaries[outcome] : null;
  const withSummary = (shell) => (
    <>
      {shell}
      {summary && !embedded && (
        <ScenarioSummary summary={summary} audience={audience}
                          onReset={() => {/* trigger scenario reset by parent via ResetToken */
                            setOutcome(null); setActiveApp(wf.entry || "home");
                            setRoute([]); setDraft(null); setWarning(null);
                            setReplacement(null); setConfirmed(false); setSettled(false);
                            setLinkForCompose(null); setChannelExtras([]);
                            setBookings((wf.bookings || []).map(b => ({ ...b })));
                            setNotepad(wf.notepad?.initial || {});
                            setIntroStep(0); setIntroDone(!hasIntro);
                          }}
                          onChooseAnother={() =>
                            navigate(audience === "public"
                                       ? "/demo-business/scenarios"
                                       : "/promoter/demos")} />
      )}
    </>
  );

  // 1) Scripted image intro (walk-in): runs before any UI.
  if (hasIntro && !introDone) {
    return (
      <WalkInIntro frames={wf.intro} step={introStep} setStep={setIntroStep}
                    autoPlay={autoPlay} resetToken={resetToken}
                    onFinish={() => {
                      setIntroDone(true);
                      if (autoPlay) onAutoPlayEnd?.();
                    }} />
    );
  }

  // 2) Phone shell: used ONLY for customer-side channels (Messenger,
  // WhatsApp, SMS, Phone) and, for chat-first scenarios, the app-picker
  // home screen so the promoter can visually swap between the customer's
  // phone and the office computer.
  const usePhoneShell = activeApp === "channel"
                          || (activeApp === "home" && wf.channel);
  if (usePhoneShell) {
    return withSummary(
      <DeviceShell title={wf.business}
                    canBack={activeApp !== "home" || route.length > 0}
                    onBack={() => (route.length > 0 ? pop() : goHome())}
                    onHome={goHome}>
        {activeApp === "home" && (
          <HomeScreen apps={availableApps} onOpen={openApp} business={wf.business} />
        )}
        {activeApp === "channel" && wf.channel && (
          <ChannelApp channel={wf.channel} resetToken={resetToken} embedded={embedded}
                       shown={channelShown} onShownChange={setChannelShown}
                       extras={channelExtras}
                       linkForCompose={linkForCompose}
                       onSendLink={sendLinkInChannel}
                       onOpenPreview={openConfirmationPreview}
                       onOpenNotepad={() => openApp("notepad")} />
        )}
      </DeviceShell>
    );
  }

  // 3) Public-facing surfaces (Business Page + full Website).  These are
  //    what a CUSTOMER sees, so the device toggle just changes the shell
  //    (customer's laptop vs. customer's phone).  When the guest submits
  //    a booking request the promoter is dropped into the office computer
  //    (dashboard) so they can show the inquiry landing in Booking Intake.
  if (activeApp === "business-page") {
    const bpContent = (
      <BusinessPageApp business={wf.publicBusiness}
                        onSubmitInquiry={(payload) => {
                          // Sandbox: nothing hits the server.  We just
                          // seed a fake incoming draft and jump the
                          // promoter to the desktop dashboard.
                          const _p = payload; void _p;
                          setOutcome("submitted");
                          if (wf.onSubmitSwitchTo) openApp(wf.onSubmitSwitchTo);
                        }} />
    );
    return deviceMode === "phone"
      ? withSummary(<DeviceShell title={wf.business} onBack={() => {}} onHome={() => {}}>{bpContent}</DeviceShell>)
      : withSummary(<DesktopShell business={wf.business} activeApp="dashboard" onSwitchApp={openApp}>{bpContent}</DesktopShell>);
  }
  if (activeApp === "website") {
    const wsContent = (
      <WebsiteApp website={wf.websiteData}
                   onOpenBookingPage={() => openApp("business-page")} />
    );
    return deviceMode === "phone"
      ? withSummary(<DeviceShell title={wf.business} onBack={() => {}} onHome={() => {}}>{wsContent}</DeviceShell>)
      : withSummary(<DesktopShell business={wf.business} activeApp="dashboard" onSwitchApp={openApp}>{wsContent}</DesktopShell>);
  }

  // 4) Business-side surface: same content regardless of chosen device.
  //    Desktop mode → browser-window DesktopShell (office computer look).
  //    Phone mode   → mobile PWA look via the phone DeviceShell.
  //    Scenario state (bookings, warnings, draft, current step) is held
  //    outside these shells so switching mid-scenario is state-preserving.
  const businessContent = (
    <>
      {activeApp === "notepad" && (
        <NotepadApp placeholder={wf.notepad?.placeholder}
                     values={notepad} onChange={setNotepad}
                     onCreateDraft={createDraftFromNotepad}
                     onOpenDashboard={() => openApp("dashboard")} />
      )}
      {activeApp === "dashboard" && (
        <DashboardApp wf={wf}
                       bookings={bookings} draft={draft} warning={warning}
                       vehicles={vehicles} route={route} push={push} pop={pop}
                       onPromoteDraft={promoteDraftToBooking}
                       onGenerateLink={generateConfirmationLink}
                       onCopyLink={copyConfirmationLink}
                       onSendConfirmation={sendConfirmation}
                       onCustomerConfirm={customerConfirms}
                       onCustomerRequestChange={customerRequestsChange}
                       onCustomerDecline={customerDeclines}
                       onCreateConflictingDraft={createConflictingDraft}
                       onReassign={reassignVehicle}
                       onMarkMaintenance={markMaintenance}
                       onRecordBalance={recordBalancePaid}
                       confirmed={confirmed} settled={settled}
                       replacement={replacement} />
      )}
    </>
  );

  if (deviceMode === "phone") {
    return withSummary(
      <DeviceShell title={wf.business}
                    canBack={route.length > 0 || activeApp !== (wf.entry || "home")}
                    onBack={() => (route.length > 0 ? pop() : goHome())}
                    onHome={goHome}>
        {businessContent}
      </DeviceShell>
    );
  }

  return withSummary(
    <DesktopShell business={wf.business}
                   activeApp={activeApp}
                   onSwitchApp={openApp}
                   onExit={wf.channel ? goHome
                             : hasIntro ? () => setIntroDone(false)
                             : undefined}>
      {businessContent}
    </DesktopShell>
  );
}

/* ---------- helpers ---------- */
function pickTruthy(src, map) {
  const out = {};
  for (const k of Object.keys(map)) {
    const v = src[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") out[map[k]] = v;
  }
  return out;
}
function replacementApplies(booking, wf) {
  // Simple heuristic: apply to any booking that references the impacted vehicle.
  const badVehicle = wf.onMarkMaintenance?.affected?.[0]?.customer || wf.bannerWarn?.affected?.[0]?.customer;
  return badVehicle && booking.customer === badVehicle;
}
function resolveApp(key, wf) {
  if (key === "channel") {
    const meta = CHANNEL_META[wf.channel?.kind];
    if (!meta) return null;
    return { key: "channel", Icon: meta.Icon, color: meta.color, label: meta.label };
  }
  return APPS[key];
}

/* ---------- HOME ---------- */
function HomeScreen({ apps, onOpen, business }) {
  const items = apps.filter(Boolean);
  return (
    <div data-testid="home-screen" className="pt-2">
      <div className="text-center text-[11px] text-slate-500 mb-3">Tap an app to open</div>
      <div className="grid grid-cols-3 gap-4">
        {items.map(a => (
          <button key={a.key} onClick={() => onOpen(a.key)}
                  data-testid={`app-icon-${a.key}`}
                  className="flex flex-col items-center gap-1.5 group">
            {a.img ? (
              <img src={a.img} alt={a.label}
                    className="w-14 h-14 rounded-2xl shadow-md group-active:scale-95 transition-transform object-cover" />
            ) : (
              <span className={`w-14 h-14 rounded-2xl ${a.color} flex items-center justify-center text-white shadow-md group-active:scale-95 transition-transform`}>
                <a.Icon className="w-6 h-6" />
              </span>
            )}
            <span className="text-[10px] text-slate-700 text-center leading-tight">{a.label}</span>
          </button>
        ))}
      </div>
      <div className="mt-6 rounded-lg bg-white/70 border border-slate-200 p-2 text-[11px] text-slate-600 text-center">
        Fake device · {business}
      </div>
    </div>
  );
}

/* ---------- CHANNEL APP ---------- */
// Compresses the scripted message timestamps so the conversation plays
// noticeably faster while keeping messages' relative pacing to each other.
const CHAT_SPEED_FACTOR = 0.4;

function ChannelApp({ channel, resetToken, shown, onShownChange, embedded = false, extras = [], linkForCompose, onSendLink, onOpenPreview, onOpenNotepad }) {
  const meta = CHANNEL_META[channel.kind] || CHANNEL_META.messenger;
  const timers = useRef([]);
  const scrollRegionRef = useRef(null);

  // Keeps the newest message in view as the conversation plays, the same
  // way a real chat app auto-follows: scoped to the embedded walkthrough
  // so standalone/promoter views keep their normal full-page scroll.
  useEffect(() => {
    if (!embedded) return;
    const region = scrollRegionRef.current;
    if (region) region.scrollTop = region.scrollHeight;
  }, [embedded, shown, extras.length, linkForCompose]);
  // Faster pacing + "resume instead of replay" are scoped to the homepage
  // embed only (`embedded`: sourced from a URL flag, see PublicScenarioStage
  // / DemoStage) so the promoter/company-dashboard demo tool keeps its
  // original pacing and always replays the conversation from message 1,
  // unchanged. When embedded, resumes from wherever `shown` already is
  // (lifted to the parent so it survives navigating to another app and
  // back) instead of replaying from scratch: only messages not yet
  // revealed get scheduled. Deliberately NOT depending on `shown` beyond
  // mount: it should capture the value once per mount (this component
  // fully unmounts/remounts on app navigation) and not reschedule every
  // time a message reveals.
  useEffect(() => {
    timers.current.forEach(clearTimeout);
    const all = channel.messages || [];
    const startFrom = embedded ? shown : 0;
    if (startFrom >= all.length) return;
    const speedFactor = embedded ? CHAT_SPEED_FACTOR : 1;
    const floor = embedded ? 250 : 0;
    const prevAt = startFrom > 0 ? (all[startFrom - 1].at || 0) : 0;
    let cumulative = 0;
    timers.current = all.slice(startFrom).map((m, idx) => {
      const priorAt = idx === 0 ? prevAt : (all[startFrom + idx - 1].at || 0);
      const gap = Math.max((m.at || 400 * (startFrom + idx + 1)) - priorAt, floor) * speedFactor;
      cumulative += gap;
      const revealIndex = startFrom + idx + 1;
      return setTimeout(() => onShownChange(v => Math.max(v, revealIndex)), cumulative);
    });
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel.messages, resetToken, embedded]);

  // Detect whether the latest extras contain the link staff bubble, if so
  // the customer sees a "Open confirmation" affordance.
  const linkSent = extras.some(e => e.key === "staff-link");

  return (
    <div data-testid={`channel-app-${channel.kind}`} className="rounded-xl bg-white border border-slate-200 overflow-hidden">
      <div className={`flex items-center gap-2 px-3 py-2 ${meta.color} text-white`}>
        <meta.Icon className="w-4 h-4" />
        <div className="text-sm font-semibold flex-1">{meta.label}</div>
        <div className="text-[10px] opacity-80">{channel.customer}</div>
      </div>
      <div ref={scrollRegionRef} className={`p-3 space-y-2 min-h-[300px] bg-slate-50 ${embedded ? "max-h-[380px] overflow-y-auto overscroll-contain" : ""}`}>
        {(channel.messages || []).slice(0, shown).map((m, i) => (
          <ChannelBubble key={i} m={m} meta={meta} />
        ))}
        {shown < (channel.messages || []).length && <div className="text-[11px] text-slate-400">typing…</div>}
        {extras.map(e => <ChannelBubble key={e.key} m={e} meta={meta} />)}
        {linkSent && (
          <div className="flex justify-start">
            <button onClick={onOpenPreview}
                    data-testid="channel-open-preview"
                    className={`text-[11px] rounded-full px-3 py-1.5 ${meta.bubble} border border-white/40 font-semibold`}>
              Tap to open confirmation
            </button>
          </div>
        )}
        <div />
      </div>
      {/* Compose area: becomes the "paste and send" flow after Copy */}
      {linkForCompose ? (
        <div className="p-3 border-t border-slate-200 bg-white" data-testid="channel-compose">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Ready to send</div>
          <div className="rounded-lg bg-slate-50 border border-slate-300 px-2.5 py-2 text-xs font-mono text-slate-800 break-all">
            {linkForCompose}
          </div>
          <button onClick={onSendLink}
                  data-testid="channel-send-link"
                  className={`mt-2 w-full text-sm font-medium rounded-full ${meta.color} text-white py-2 inline-flex items-center justify-center gap-1`}>
            <Send className="w-3.5 h-3.5" /> Send in {meta.label}
          </button>
        </div>
      ) : (
        <div className="p-3 border-t border-slate-200 bg-white flex items-center justify-between gap-2">
          <div className="text-[11px] text-slate-500">
            {embedded && shown < (channel.messages || []).length ? "Reading the conversation…" : "Customer contact stays here."}
          </div>
          {(!embedded || shown >= (channel.messages || []).length) && (
            <button onClick={onOpenNotepad}
                    data-testid="channel-open-notepad"
                    className="text-xs rounded-full bg-[#1B4332] text-white px-3 py-1.5 font-medium inline-flex items-center gap-1">
              Record in Notepad <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
function ChannelBubble({ m, meta }) {
  if (m.from === "system") {
    return <div className="text-center text-[10px] text-slate-500 italic">{m.text}</div>;
  }
  const right = m.from === "staff";
  return (
    <div className={`flex ${right ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs ${right ? "bg-slate-800 text-white" : meta.bubble}`}>
        <div className="text-[9px] uppercase tracking-wider opacity-70 mb-0.5">{right ? "Staff" : "Customer"}</div>
        {m.text}
      </div>
    </div>
  );
}

/* ---------- NOTEPAD APP ---------- */
function NotepadApp({ placeholder = {}, values, onChange, onCreateDraft, onOpenDashboard }) {
  // Real ConfirmPH Notepad (StaffNotepad) is a single free-text textarea plus
  // a source dropdown and one Save/Convert button.  We mirror that shape here.
  // The demo still needs typed fields to populate the Draft, so `values.raw`
  // holds the promoter's freeform note and, on Convert, we extract the
  // scenario-known fields from placeholder into the draft.
  const source = values._source || placeholder.contact || "messenger";
  const setRaw = (raw) => onChange({ ...values, raw });
  const setSource = (s) => onChange({ ...values, _source: s });
  const suggested =
    `${placeholder.customer || "Customer name"}, mobile #, ` +
    `${placeholder.vehicle || "vehicle"}, ` +
    `${placeholder.pickup || "pickup date/time"} to ${placeholder.ret || "return date/time"}, ` +
    `pickup ${placeholder.pickup_location || "location"}, ` +
    `${placeholder.price || "rate"}, deposit ${placeholder.deposit || "amount"}${placeholder.notes ? ". Notes: " + placeholder.notes : ""}.`;
  const convert = () => {
    // Feed scenario placeholder values into the created draft so the workflow
    // continues to demonstrate a fully-populated booking.
    onChange({ ...values, ...placeholder });
    onCreateDraft();
  };
  return (
    <div data-testid="notepad-app" className="min-h-full bg-[#FAFAFA] rounded-xl border border-slate-200 overflow-hidden">
      {/* Sticky header matches StaffNotepad */}
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="px-4 py-3 flex items-center gap-3">
          <button onClick={onOpenDashboard}
                   data-testid="notepad-open-dashboard"
                   className="text-slate-500 hover:text-slate-900"
                   title="Back to Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-base font-semibold leading-tight truncate">
              {placeholder._business || "Demo"} Notes
            </h1>
            <p className="text-xs text-emerald-700 inline-flex items-center gap-1">
              <Wifi className="w-3 h-3" /> Online (demo)
            </p>
          </div>
        </div>
      </header>

      <main className="px-4 py-4 space-y-4 max-w-2xl mx-auto">
        <section className="space-y-3 bg-white rounded-xl border border-slate-200 p-4">
          <textarea
            value={values.raw || ""}
            onChange={e => setRaw(e.target.value)}
            data-testid="notepad-textarea"
            placeholder="Type or paste booking details here…"
            className="w-full min-h-[160px] rounded-lg border border-slate-300 p-3 text-sm focus:border-[#2D6A4F] outline-none"
          />
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <select value={source} onChange={e => setSource(e.target.value)}
                     data-testid="notepad-source"
                     className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm">
              {[
                ["messenger","Messenger"],["whatsapp","WhatsApp"],["viber","Viber"],
                ["sms","SMS"],["facebook","Facebook"],["phone","Phone call"],
                ["walk_in","Walk-in"],["quick_note","Quick note"],["other","Other"],
              ].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button onClick={convert}
                     data-testid="notepad-create-draft"
                     className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-emerald-900">
              <ArrowRight className="w-4 h-4" /> Convert to Booking Draft
            </button>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-[11px] text-slate-600">
            <span className="uppercase tracking-wider font-semibold text-slate-500">Suggested from chat</span>
            <div className="mt-0.5 text-slate-700 italic">{suggested}</div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* ---------- DASHBOARD APP ---------- */
function DashboardApp({
  wf, bookings, draft, warning, vehicles, route, push, pop,
  onPromoteDraft, onGenerateLink, onCopyLink,
  onSendConfirmation, onCustomerConfirm, onCustomerRequestChange, onCustomerDecline,
  onCreateConflictingDraft, onReassign, onMarkMaintenance, onRecordBalance,
  confirmed, settled, replacement,
}) {
  const top = route[route.length - 1];

  if (top?.startsWith("draft:") && draft) return (
    <DraftDetail wf={wf} draft={draft} warning={warning}
                  onBack={pop} onPromote={onPromoteDraft}
                  onReassign={onReassign} />
  );
  if (top?.startsWith("send-link:")) {
    const id = top.split(":")[1];
    const b = bookings.find(x => x.id === id);
    if (b) return (
      <SendLinkScreen b={b} onBack={pop} onCopy={() => onCopyLink(b.id)}
                       hasChannel={!!wf.channel} />
    );
  }
  if (top?.startsWith("booking:")) {
    const id = top.split(":")[1];
    const b = bookings.find(x => x.id === id);
    if (b) return (
      <BookingDetail b={b} onBack={pop}
                      onGenerateLink={() => onGenerateLink(b.id)}
                      onSendConfirmation={() => onSendConfirmation(b.id)}
                      onRecordBalance={() => onRecordBalance(b.id)}
                      isDeposit={wf.kind === "financial"} />
    );
  }
  if (top === "confirmation-preview") {
    return <CustomerConfirmScreen bookings={bookings}
                                    onConfirm={(bId) => onCustomerConfirm(bId)}
                                    onRequestChange={(bId) => onCustomerRequestChange(bId)}
                                    onDecline={(bId) => onCustomerDecline(bId)}
                                    onBack={pop} />;
  }

  return (
    <DashboardHome wf={wf} bookings={bookings} draft={draft} warning={warning}
                     vehicles={vehicles} onOpenDraft={() => push(`draft:${draft.id}`)}
                     onOpenBooking={(id) => push(`booking:${id}`)}
                     onCreateConflictingDraft={onCreateConflictingDraft}
                     onReassign={onReassign}
                     onMarkMaintenance={onMarkMaintenance}
                     onOpenWarning={() => push("warning")}
                     confirmed={confirmed} settled={settled} replacement={replacement} />
  );
}

export function DashboardHome({
  wf, bookings, draft, warning, vehicles, onOpenDraft, onOpenBooking,
  onCreateConflictingDraft, onReassign, onMarkMaintenance,
  confirmed, settled, replacement,
}) {
  // Keep this demo surface aligned with the real Dashboard.jsx information
  // hierarchy. All values are still derived from isolated demo state.
  const pendingCount   = bookings.filter(b => /pending|sent|risk/i.test(b.status || "")).length;
  const availCount     = vehicles.filter(v => /available/i.test(v.status || "")).length;
  const inUseCount     = vehicles.filter(v => /in.?use|rented/i.test(v.status || "")).length;
  const maintenanceCount = vehicles.filter(v => /maintenance/i.test(v.status || "")).length;
  const inspectionCount  = vehicles.filter(v => /inspection/i.test(v.status || "")).length;
  const todayPickups = bookings.filter(b => /today/i.test(b.pickup || "")).length;
  const todayReturns = bookings.filter(b => /today/i.test(b.ret || "")).length;
  const problemCount = warning ? 1 : 0;

  return (
    <div data-testid="dashboard-app" className="space-y-5">
      {/* Current real dashboard header: page title, purpose and primary action. */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-950">Today</h1>
          <p className="mt-0.5 text-xs text-slate-600">What needs attention across {wf.business}</p>
        </div>
        <button type="button" disabled
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#1B4332] px-3 py-2 text-xs font-semibold text-white opacity-80"
                title="Not part of this demo scenario">
          <Plus className="w-3.5 h-3.5" /> Add booking
        </button>
      </div>

      {/* Tier 1: same "Requires attention" card used by the current homepage. */}
      <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="demo-attention-feed">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-950">Requires attention</h2>
          <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Handle the urgent items first</p>
        </div>

      {warning ? (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 border-l-4 border-l-amber-500" data-testid="dashboard-warning">
          <div className="flex items-start gap-2 text-amber-900 text-xs">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="font-semibold">{warning.warn}</span>
          </div>
          {warning.affected?.length > 0 && (
            <div className="mt-2 rounded bg-white border border-amber-200 p-2 text-xs">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Affected bookings</div>
              {warning.affected.map((a, i) => <div key={i} className="text-slate-900 mt-0.5">{a.customer} · {a.when}</div>)}
            </div>
          )}
          {warning.options?.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-slate-800">
              {warning.options.map((o, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-amber-500" />{o}
                </li>
              ))}
            </ul>
          )}
          {(wf.reassignedVehicle || wf.onMarkMaintenance?.replacement || wf.bannerWarn?.replacement) && !replacement && (
            <ReplacementChooser wf={wf} onReassign={onReassign} />
          )}
        </div>
      ) : draft ? (
        <button onClick={onOpenDraft}
                data-testid="dashboard-draft-card"
                className="mt-3 w-full text-left rounded-lg border border-emerald-300 bg-emerald-50/50 p-3 hover:bg-emerald-50 border-l-4 border-l-emerald-500">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">
            <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" /> New booking draft
          </div>
          <div className="text-sm text-slate-900 mt-0.5 font-medium">{draft.customer}</div>
          <div className="text-[11px] text-slate-600 mt-0.5">{draft.vehicle} · {draft.pickup} → {draft.ret}</div>
        </button>
      ) : (
        <div className="py-5 text-center text-xs text-slate-500" data-testid="demo-attention-empty">
          <CheckCircle2 className="mx-auto mb-1.5 h-6 w-6 text-emerald-500" /> Nothing urgent right now
        </div>
      )}
      </section>

      {/* Tier 2: current Today workspace tiles. */}
      <section>
        <h2 className="mb-2 font-display text-base font-semibold text-slate-950">Today</h2>
        <div className="grid grid-cols-2 gap-2" data-testid="dashboard-stats">
          <DemoWorkTile icon={Truck} label="Today's pickups" value={todayPickups} tone="blue" />
          <DemoWorkTile icon={RotateCcw} label="Today's returns" value={todayReturns} tone="indigo" />
          <DemoWorkTile icon={ClipboardCheck} label="Needs confirmation" value={pendingCount} tone="amber" />
          <DemoWorkTile icon={AlertOctagon} label="Problems" value={problemCount} tone={problemCount ? "red" : "slate"} />
        </div>
      </section>

      {/* A draft remains actionable even while another warning owns the top
          attention position. This mirrors the dashboard's lower intake area. */}
      {warning && draft && (
        <button onClick={onOpenDraft}
                data-testid="dashboard-draft-card"
                className="w-full text-left rounded-lg border border-emerald-300 bg-white p-3 hover:bg-emerald-50 border-l-4 border-l-emerald-500">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">
            <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" /> New draft
          </div>
          <div className="text-sm text-slate-900 mt-0.5 font-medium">{draft.customer}</div>
          <div className="text-[11px] text-slate-600 mt-0.5">{draft.vehicle}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{draft.pickup} → {draft.ret}</div>
        </button>
      )}

      {/* Tier 3: current Fleet snapshot. */}
      <section>
        <h2 className="mb-2 font-display text-base font-semibold text-slate-950">Fleet snapshot</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="fleet-snapshot">
          <DemoSnapshotTile label="Available" value={availCount} tone="green" />
          <DemoSnapshotTile label="In Use" value={inUseCount} tone="blue" />
          <DemoSnapshotTile label="Maintenance" value={maintenanceCount} tone="amber" />
          <DemoSnapshotTile label="Needs Inspection" value={inspectionCount} tone="red" />
        </div>
      </section>

      {bookings.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-display text-base font-semibold text-slate-950">Upcoming bookings</div>
            <div className="text-[10px] text-slate-500">{bookings.length}</div>
          </div>
          <div className="space-y-2">
            {bookings.map(b => (
              <button key={b.id} onClick={() => onOpenBooking(b.id)}
                      data-testid={`dashboard-booking-card-${b.id}`}
                      className="w-full text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-emerald-300">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900 truncate">{b.customer}</div>
                  <StatusChip status={b.status} />
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-600 mt-1">
                  <Car className="w-3 h-3" />{b.vehicle}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                  <ArrowRight className="w-3 h-3" />{b.pickup} → {b.ret}
                </div>
                {b.source && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] rounded-full bg-slate-100 text-slate-700 px-1.5 py-0.5">
                    {b.source}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {vehicles.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Vehicles</div>
          <div className="space-y-2">
            {vehicles.map(v => (
              <div key={v.plate} className="rounded-lg border border-slate-200 bg-white p-2.5" data-testid={`dashboard-vehicle-${v.plate}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Car className="w-4 h-4 text-slate-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{v.model}</div>
                    <div className="text-[10px] font-mono text-slate-500">{v.plate}</div>
                  </div>
                  <VehicleStatusChip status={v.status} />
                </div>
                {v.canMarkMaintenance && (
                  <button onClick={() => onMarkMaintenance(v.plate)}
                          data-testid={`dashboard-mark-maintenance-${v.plate}`}
                          className="mt-2 w-full rounded-md bg-amber-600 text-white text-[11px] py-1 font-medium inline-flex items-center justify-center gap-1">
                    <Wrench className="w-3 h-3" /> Report breakdown
                  </button>
                )}
                {v.incidentPhoto && (
                  <div className="mt-2 rounded-md border border-rose-200 overflow-hidden"
                       data-testid={`dashboard-vehicle-incident-${v.plate}`}>
                    <div className="flex items-center gap-1 px-2 py-1 bg-rose-50 text-[10px] font-semibold text-rose-900 uppercase tracking-wider">
                      <AlertTriangle className="w-3 h-3" /> Incident photo
                    </div>
                    <img src={v.incidentPhoto.url}
                         alt={v.incidentPhoto.caption || "Damage photo"}
                         className="w-full aspect-[4/3] object-cover" />
                    {v.incidentPhoto.caption && (
                      <div className="px-2 py-1 text-[10px] text-slate-600 bg-white">
                        {v.incidentPhoto.caption}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {wf.incomingDraft && !draft && (
        <button onClick={onCreateConflictingDraft}
                data-testid="dashboard-add-incoming-draft"
                className="w-full rounded-lg border border-dashed border-slate-400 bg-white/70 p-2.5 text-xs text-slate-700 hover:bg-white">
          + New booking inquiry ({wf.incomingDraft.customer})
        </button>
      )}

      {confirmed && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 border-l-4 border-l-emerald-500 flex items-center gap-2 text-emerald-800 text-sm font-semibold" data-testid="dashboard-confirmed-banner">
          <CheckCircle2 className="w-4 h-4" /> Customer Confirmed
        </div>
      )}
      {settled && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 border-l-4 border-l-emerald-500 text-emerald-800 text-sm font-semibold" data-testid="dashboard-settled-banner">
          {wf.finalOutcome || "Paid: recorded"}
        </div>
      )}
    </div>
  );
}

function DemoWorkTile({ icon: Icon, label, value, tone }) {
  const tones = {
    blue:   "bg-blue-50 border-blue-100 text-blue-900",
    indigo: "bg-indigo-50 border-indigo-100 text-indigo-900",
    amber:  "bg-amber-50 border-amber-100 text-amber-900",
    red:    "bg-rose-50 border-rose-100 text-rose-900",
    slate:  "bg-slate-50 border-slate-200 text-slate-700",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] uppercase tracking-[0.14em] font-semibold opacity-80">{label}</span>
        <Icon className="h-3.5 w-3.5 opacity-70" />
      </div>
      <div className="mt-1 text-xl font-semibold leading-none">{value}</div>
    </div>
  );
}

function DemoSnapshotTile({ label, value, tone }) {
  const tones = {
    green: "bg-emerald-50 border-emerald-100 text-emerald-900",
    blue:  "bg-blue-50 border-blue-100 text-blue-900",
    amber: "bg-amber-50 border-amber-100 text-amber-900",
    red:   "bg-rose-50 border-rose-100 text-rose-900",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="text-xl font-semibold leading-none">{value}</div>
      <div className="mt-1 text-[9px] uppercase tracking-[0.14em] font-semibold opacity-80">{label}</div>
    </div>
  );
}

function VehicleStatusChip({ status }) {
  const s = (status || "").toLowerCase();
  const tone = s.includes("available") ? "bg-emerald-100 text-emerald-800"
             : s.includes("maintenance") || s.includes("problem") || s.includes("repair") ? "bg-amber-100 text-amber-800"
             : s.includes("overdue") || s.includes("issue") ? "bg-rose-100 text-rose-800"
             : "bg-slate-100 text-slate-700";
  return <span className={`text-[9px] rounded-full px-1.5 py-0.5 font-medium ${tone}`}>{status}</span>;
}

function DraftDetail({ draft, warning, onBack, onPromote, onReassign }) {
  return (
    <div data-testid="draft-detail" className="space-y-3">
      <button onClick={onBack} className="text-xs text-slate-500 inline-flex items-center gap-1">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to Dashboard
      </button>
      <div className="rounded-lg border border-emerald-300 bg-white p-3">
        <div className="text-xs uppercase tracking-wider text-emerald-800 font-semibold">Draft</div>
        <KV rows={[
          ["Customer", draft.customer], ["Vehicle", draft.vehicle],
          ["Pickup",   draft.pickup],    ["Return",  draft.ret],
          ["Price",    draft.price],     ["Deposit", draft.deposit],
          ["Balance",  draft.balance],   ["Source",  draft.source],
          ["Status",   draft.status],
        ]} />
      </div>
      {warning && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="draft-conflict">
          <div className="flex items-start gap-2 text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{warning.warn}</span>
          </div>
          <ReplacementChooser wf={{ ...(warning || {}), reassignedVehicle: warning.replacement || {}, bannerWarn: warning }}
                                inline
                                testIdPrefix="draft"
                                onReassign={onReassign} />
        </div>
      )}
      {!warning && (
        <button onClick={onPromote}
                data-testid="draft-create-booking"
                className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2">
          Create Booking
        </button>
      )}
    </div>
  );
}

function BookingDetail({ b, onBack, onGenerateLink, onSendConfirmation, onRecordBalance, isDeposit }) {
  const needsLink = b.status === "Awaiting confirmation";
  const linkReady = b.status === "Confirmation link ready";
  const sent      = b.status === "Confirmation sent";
  return (
    <div data-testid="booking-detail" className="space-y-3">
      <button onClick={onBack} className="text-xs text-slate-500 inline-flex items-center gap-1">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to Dashboard
      </button>
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Booking</div>
          <StatusChip status={b.status} />
        </div>
        <KV rows={[
          ["Customer", b.customer], ["Vehicle", b.vehicle],
          ["Pickup",   b.pickup],    ["Return",  b.ret],
          ["Subtotal", b.subtotal],  ["Deposit", b.deposit],
          ["Balance",  b.balance],   ["Source",  b.source],
        ]} />
      </div>

      {!isDeposit && needsLink && (
        <>
          <div className="rounded-lg border border-amber-300 bg-amber-50 border-l-4 border-l-amber-500 p-3 text-xs text-amber-900" data-testid="booking-awaiting-confirmation">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold">Awaiting confirmation</div>
                <div className="mt-0.5">Send the customer their confirmation link so they can review and confirm the booking.</div>
              </div>
            </div>
          </div>
          <button onClick={onGenerateLink}
                   data-testid="booking-send-confirmation"
                   className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2 hover:bg-emerald-900">
            Send Confirmation Link
          </button>
        </>
      )}
      {!isDeposit && linkReady && (
        <button onClick={onGenerateLink}
                data-testid="booking-view-link"
                className="w-full rounded-lg bg-emerald-700 text-white text-sm font-medium py-2">
          View / Re-copy confirmation link
        </button>
      )}
      {!isDeposit && sent && (
        <div className="text-[11px] text-slate-500 italic">Link sent: waiting for customer to review…</div>
      )}
      {!isDeposit && b.status === "Change requested" && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 border-l-4 border-l-amber-500 p-3 text-xs text-amber-900">
          Customer requested a change. Follow up via the source channel.
        </div>
      )}
      {!isDeposit && b.status === "Declined by customer" && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 border-l-4 border-l-rose-500 p-3 text-xs text-rose-900">
          Customer declined the booking. Free the vehicle for other inquiries.
        </div>
      )}
      {isDeposit && b.balance && b.balance !== "₱0" && (
        <button onClick={onRecordBalance}
                data-testid="booking-record-balance"
                className="w-full rounded-lg bg-emerald-700 text-white text-sm font-medium py-2">
          Record balance paid
        </button>
      )}
    </div>
  );
}

function SendLinkScreen({ b, onBack, onCopy, hasChannel }) {
  const url = `confirmph.app/c/DEMO-${b.id.replace(/[^a-z0-9]/gi, "").toUpperCase()}`;
  return (
    <div data-testid="send-link-screen" className="space-y-3">
      <button onClick={onBack} className="text-xs text-slate-500 inline-flex items-center gap-1">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to Booking
      </button>
      <div className="rounded-lg border border-emerald-300 bg-white p-3 border-l-4 border-l-emerald-500">
        <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Confirmation link ready</div>
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2 flex items-center gap-2">
          <code data-testid="send-link-url" className="text-[11px] font-mono text-slate-800 truncate flex-1">{url}</code>
          <button onClick={onCopy}
                   data-testid="send-link-copy"
                   className="text-xs px-3 py-1.5 rounded-full bg-[#1B4332] text-white font-medium">
            Copy
          </button>
        </div>
        <div className="mt-2 text-[11px] text-slate-600">
          {hasChannel
            ? "Tap Copy: you'll be brought back to the customer's chat with the link ready to send."
            : "Tap Copy: the customer will then see their confirmation page."}
        </div>
        <div className="mt-2 text-[11px] text-slate-700 rounded bg-amber-50 border border-amber-200 p-2">
          <b>{b.customer}</b> · {b.vehicle}
          <div className="mt-0.5">{b.pickup} → {b.ret}</div>
        </div>
      </div>
    </div>
  );
}

function CustomerConfirmScreen({ bookings, onConfirm, onRequestChange, onDecline, onBack }) {
  const b = bookings.find(x => x.status === "Confirmation sent") || bookings[bookings.length - 1];
  return (
    <div data-testid="customer-confirm-screen" className="space-y-3">
      <button onClick={onBack} className="text-xs text-slate-500 inline-flex items-center gap-1">
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </button>
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-slate-700" />
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Customer view</div>
        </div>
        <div className="text-sm font-semibold text-slate-900">Confirm your booking</div>
        <div className="mt-2 text-xs text-slate-700">This is what the customer sees on their phone.</div>
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs">
          <div><b>{b.customer}</b> · {b.vehicle}</div>
          <div className="mt-1">{b.pickup} → {b.ret}</div>
          {b.deposit && <div className="mt-1">Deposit: {b.deposit} · Balance: {b.balance}</div>}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2">
          <button onClick={() => onConfirm(b.id)}
                   data-testid="customer-confirm-btn"
                   className="w-full rounded-full bg-emerald-700 text-white text-sm font-medium py-2">
            Simulate customer taps Confirm
          </button>
          <button onClick={() => onRequestChange(b.id)}
                   data-testid="customer-request-change-btn"
                   className="w-full rounded-full bg-white border border-amber-400 text-amber-900 text-sm font-medium py-2">
            Simulate Request Change
          </button>
          <button onClick={() => onDecline(b.id)}
                   data-testid="customer-decline-btn"
                   className="w-full rounded-full bg-white border border-rose-400 text-rose-800 text-sm font-medium py-2">
            Simulate Decline
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const s = (status || "").toLowerCase();
  const tone = /declined/i.test(s) ? "bg-rose-100 text-rose-800"
             : /change requested/i.test(s) ? "bg-amber-100 text-amber-800"
             : /awaiting|link ready|sent|pending|risk/i.test(s) ? "bg-amber-100 text-amber-800"
             : /confirm/i.test(s) ? "bg-emerald-100 text-emerald-800"
             : /paid|record/i.test(s) ? "bg-emerald-100 text-emerald-800"
             : /overdue|reassigned|change/i.test(s) ? "bg-rose-100 text-rose-800"
             : "bg-slate-100 text-slate-700";
  return <span className={`text-[10px] rounded-full px-2 py-0.5 font-medium ${tone}`}>{status}</span>;
}

function KV({ rows }) {
  return (
    <dl className="mt-1 text-xs space-y-1">
      {rows.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <dt className="w-20 text-slate-500 uppercase tracking-wider text-[10px]">{k}</dt>
          <dd className="flex-1 text-slate-900">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// ReplacementChooser: the demo mirror of the real ReassignVehicleModal chooser.
//
// The real Business modal (`components/ReassignVehicleModal.jsx`) now opens
// on a "How would you like to choose a replacement?" screen with Smart
// Suggest and Choose Manually.  We mirror the same shape here (compact,
// because the demo card is small) so the promoter can demonstrate BOTH
// paths without the demo drifting away from the real product.
//
// Scenario data can define `wf.replacementCandidates`: an array of
// { model, plate, status } entries, for the manual fleet picker.  If
// missing, we render a small default fleet so the picker still teaches.
// ---------------------------------------------------------------------------
function ReplacementChooser({ wf, onReassign, testIdPrefix = "dashboard", inline = false }) {
  const [mode, setMode] = useState(null); // null | "smart" | "manual"
  const smart = wf.reassignedVehicle
                  || wf.conflict?.replacement
                  || wf.onMarkMaintenance?.replacement
                  || wf.bannerWarn?.replacement;
  const fleet = wf.replacementCandidates || [
    smart && { ...smart, status: "Available", categoryMatch: true, recommended: true },
    { model: "Mitsubishi Mirage", plate: "LMN-901", status: "Available" },
    { model: "Honda City",         plate: "HCT-4412", status: "Tight buffer" },
    { model: "Toyota Fortuner",    plate: "FRT-9911", status: "Already booked" },
    { model: "Honda Click",        plate: "HCL-1188", status: "Wrong category" },
  ].filter(Boolean);

  if (mode === "manual") {
    return (
      <div className={inline ? "mt-2" : "mt-2"}
           data-testid={`${testIdPrefix}-reassign-manual`}>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
          Pick from the fleet
        </div>
        <ul className="space-y-1">
          {fleet.map((v) => {
            const tone = v.status === "Available"       ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                        : v.status === "Tight buffer"   ? "border-amber-200 bg-amber-50 text-amber-900"
                        : v.status === "Already booked" ? "border-rose-200 bg-rose-50 text-rose-900 opacity-70"
                                                          : "border-slate-200 bg-slate-50 text-slate-600 opacity-70";
            const disabled = v.status === "Already booked" || v.status === "Wrong category";
            return (
              <li key={v.plate}>
                <button type="button" disabled={disabled}
                         onClick={() => onReassign("manual", v)}
                         data-testid={`${testIdPrefix}-reassign-candidate-${v.plate}`}
                         className={`w-full flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-[11px] ${tone} disabled:cursor-not-allowed`}>
                  <span className="flex items-center gap-1.5 font-medium">
                    <Car className="w-3 h-3" />
                    {v.model} <span className="font-mono opacity-70">{v.plate}</span>
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-semibold">{v.status}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <button type="button" onClick={() => setMode(null)}
                 data-testid={`${testIdPrefix}-reassign-back`}
                 className="mt-1.5 text-[11px] text-slate-500 hover:text-slate-800">
          ← Back to Smart / Manual
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1.5" data-testid={`${testIdPrefix}-reassign-chooser`}>
      <button type="button" onClick={() => onReassign("smart")}
               disabled={!smart}
               data-testid={`${testIdPrefix}-reassign-smart`}
               className="w-full rounded-md bg-emerald-700 text-white text-[11px] py-1.5 font-medium inline-flex items-center justify-center gap-1 disabled:opacity-60">
        <CheckCircle2 className="w-3 h-3" />
        Suggested{smart?.model ? `: ${smart.model}${smart.plate ? ` · ${smart.plate}` : ""}` : ""}
        {!smart && <span className="opacity-70">: No suitable replacement found</span>}
      </button>
      <button type="button" onClick={() => setMode("manual")}
               data-testid={`${testIdPrefix}-reassign-manual-btn`}
               className="w-full rounded-md border border-slate-300 bg-white text-slate-800 text-[11px] py-1.5 font-medium inline-flex items-center justify-center gap-1">
        <ClipboardList className="w-3 h-3" /> Choose Manually
      </button>
    </div>
  );
}
